import React, { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { CalendarDaysIcon, CheckCircleIcon, MapPinIcon, XMarkIcon } from "@heroicons/react/24/outline";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import moment from "moment";
import { AuthContext } from "../contexts/auth";
import ComboboxAutocomplete from "./combobox_autocomplete";
import EmployeesMultiSelectSearchbar from "./employee_multiselect_searchbar";
import { getEmployeesWithContractInRange } from "../services/api_calendar";
import { getWorkingAreaList, bulkAssignWorkingAreaRange } from "../services/api_working_area";
import Swal from "sweetalert2";



function WorkingAreaBulkAssignmentModal({ open, onClose, permission = null, onSave, units = [], initialStartDate = null, initialEndDate = null, }) {
    const { token } = useContext(AuthContext);

    const [workingAreas, setWorkingAreas] = useState([]);
    const [selectedWorkingArea, setSelectedWorkingArea] = useState(null);

    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [isLoadingUnitEmployees, setIsLoadingUnitEmployees] = useState(false);

    const [startDate, setStartDate] = useState(
        initialStartDate || moment().startOf("isoWeek").format("YYYY-MM-DD")
    );

    const [endDate, setEndDate] = useState(
        initialEndDate || moment().endOf("isoWeek").format("YYYY-MM-DD")
    );

    const [assignmentMode, setAssignmentMode] = useState("empty_only");

    const [isLoadingAreas, setIsLoadingAreas] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [error, setError] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    

    /* PERIODO */
    const selectedDays = useMemo(() => {
        if (!startDate || !endDate) return 0;

        const start = moment(startDate, "YYYY-MM-DD", true);
        const end = moment(endDate, "YYYY-MM-DD", true);

        if (!start.isValid() || !end.isValid()) return 0;
        if (end.isBefore(start, "day")) return 0;

        return end.diff(start, "days") + 1;
    }, [startDate, endDate]);


    const dateRangeError = useMemo(() => {
        if (!startDate || !endDate) {
            return "Seleziona il periodo.";
        }

        if (moment(endDate).isBefore(moment(startDate), "day")) {
            return "La data finale non può precedere la data iniziale.";
        }

        if (selectedDays > 31) {
            return "Il periodo massimo consentito è di 31 giorni.";
        }

        return "";
    }, [startDate, endDate, selectedDays]);


    const maxEndDate = useMemo(() => {
        if (!startDate) return undefined;

        return moment(startDate).add(30, "days").format("YYYY-MM-DD");
    }, [startDate]);

    useEffect(() => {
        if (!open) return;

        setStartDate(initialStartDate || moment().startOf("isoWeek").format("YYYY-MM-DD"));

        setEndDate(initialEndDate || moment().endOf("isoWeek").format("YYYY-MM-DD"));
    }, [open, initialStartDate, initialEndDate]);

    /* AREE */
    useEffect(() => {
        if (!open || !token) return;

        const fetchWorkingAreas = async () => {
            setIsLoadingAreas(true);
            setError("");

            try {
                const response = await getWorkingAreaList(token, {status: "active", paginate: false}, "shifts");

                setWorkingAreas(Array.isArray(response) ? response : response?.results || []);

            } catch (requestError) {
                console.error("Errore caricamento aree di lavoro:", requestError);
                setError("Non è stato possibile recuperare le aree di lavoro.");
            } finally {
                setIsLoadingAreas(false);
            }
        };

        fetchWorkingAreas();
    }, [open, token]);


    /* DIPENDENTI */
    const getEmployeesCb = useCallback(
        (
            tokenArg,
            q,
            filterBy = "hr_active",
            ouId,
            page,
            pageSize,
            permissionArg,
            start,
            end,
            sortCfg,
        ) => {
            return getEmployeesWithContractInRange(
                tokenArg,
                q,
                filterBy,
                null,
                page,
                pageSize,
                null,
                permissionArg,
                start,
                end,
                sortCfg,
                true,
            );
        },
        []
    );

    const employeeRolePairs = useMemo(() => (selectedEmployees || []).map((employee) => ({ employeeId: employee.id })), [selectedEmployees]);

    const handleSelectUnit = async (unit) => {
        if (!unit?.id) return;

        setSelectedUnit(unit);
        setIsLoadingUnitEmployees(true);
        setError("");

        try {
            const response = await getEmployeesWithContractInRange(
                token,
                null,
                "hr_active",
                null,
                1,
                1000,
                Number(unit.id),
                permission,
                startDate,
                endDate,
                null,
                true,
            );

            const employees = Array.isArray(response) ? response : response?.results || [];

            const existingIds = new Set(selectedEmployees.map((employee) => Number(employee.id)));

            const newEmployees = employees.filter((employee) => !existingIds.has(Number(employee.id)));

            setSelectedEmployees((prev) => {
                const employeeMap = new Map(
                    prev.map((employee) => [
                        Number(employee.id),
                        employee,
                    ])
                );

                employees.forEach((employee) => {
                    employeeMap.set(
                        Number(employee.id),
                        employee
                    );
                });

                return Array.from(employeeMap.values());
            });

            Swal.fire({
                icon: newEmployees.length > 0 ? "success" : "info",
                title: newEmployees.length > 0 ? "Dipendenti aggiunti" : "Nessun nuovo dipendente",
                text:
                    newEmployees.length > 0
                        ? `${newEmployees.length} ${
                            newEmployees.length === 1 ? "dipendente aggiunto" : "dipendenti aggiunti"
                        } da "${unit.name}".`
                        : `Tutti i dipendenti di "${unit.name}" sono già presenti nella selezione.`,
                timer: 2000,
                showConfirmButton: false,
                toast: true,
                position: "center",
            });

        } catch (requestError) {
            console.error("Errore caricamento dipendenti unità:", requestError);

            setError("Non è stato possibile recuperare i dipendenti dell'unità organizzativa");
        } finally {
            setSelectedUnit(null);
            setIsLoadingUnitEmployees(false);
        }
    };

    /* VALIDAZIONE */
    const canSubmit =
        selectedWorkingArea &&
        selectedEmployees.length > 0 &&
        startDate &&
        endDate &&
        !dateRangeError &&
        !isSaving;


    /* SALVATAGGIO */
    const handleSubmit = async () => {
        if (!canSubmit) return;

        setIsSaving(true);
        setError("");
        setSuccessMessage("");

        try {
            const employeeIds = selectedEmployees.map((employee) => Number(employee.id));

            const payload = {
                working_area: Number(selectedWorkingArea.id),
                employee_ids: employeeIds,
                start_date: startDate,
                end_date: endDate,
                mode: assignmentMode,
            };

            const response = await bulkAssignWorkingAreaRange(token, payload, "shifts");

            await onSave?.({startDate, endDate, employeeIds,});

            const total =
                (response?.created_count ?? 0) +
                (response?.updated_count ?? 0) +
                (response?.unchanged_count ?? 0) +
                (response?.skipped_count ?? 0);

            await Swal.fire({
                icon: total > 0 ? "success" : "info",
                title: total > 0 ? "Assegnazione completata" : "Nessuna assegnazione trovata",
                html: `
                    <div style="text-align:left; font-size:14px; line-height:1.8;">
                        <div><strong>Nuove assegnazioni:</strong> ${response?.created_count ?? 0}</div>
                        <div><strong>Assegnazioni aggiornate:</strong> ${response?.updated_count ?? 0}</div>
                        <div><strong>Già presenti:</strong> ${response?.unchanged_count ?? 0}</div>
                        <div><strong>Ignorate:</strong> ${response?.skipped_count ?? 0}</div>
                    </div>
                `,
                confirmButtonText: "OK",
            });

            resetModal();
            onClose?.();

        } catch (requestError) {
            console.error("Errore assegnazione massiva area:", requestError);

            setError(requestError?.response?.data?.detail || "Errore durante l'assegnazione massiva dell'area di lavoro");
        } finally {
            setIsSaving(false);
        }
    };

    const resetModal = () => {
        setSelectedWorkingArea(null);
        setSelectedEmployees([]);
        setSelectedUnit(null);

        setStartDate(initialStartDate || moment().startOf("isoWeek").format("YYYY-MM-DD"));

        setEndDate(initialEndDate || moment().endOf("isoWeek").format("YYYY-MM-DD"));

        setAssignmentMode("empty_only");
        setError("");
        setSuccessMessage("");
    };

    /* CHIUSURA */
    const handleClose = () => {
        if (isSaving) return;

        resetModal();
        onClose?.();
    };


    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog as="div" className="relative z-[250]" onClose={handleClose}>

                {/* BACKDROP */}
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-start justify-center px-4 pb-6 pt-10 sm:px-6">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white text-left shadow-2xl">

                                {isSaving && (
                                    <div className="absolute inset-0 z-[5000] flex items-center justify-center bg-white/70 backdrop-blur-[1px]">
                                        <div className="flex items-center gap-3 rounded-lg border border-indigo-100 bg-white px-5 py-3 shadow-lg">
                                            <CircularProgress size={20} />

                                            <div>
                                                <p className="text-sm font-semibold text-indigo-700">
                                                    Assegnazione in corso
                                                </p>

                                                <p className="mt-0.5 text-xs text-gray-500">
                                                    Attendi il completamento dell'operazione...
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                )}
                                {/* HEADER */}
                                <div className="flex shrink-0 items-center justify-between border-b border-gray-200 bg-indigo-50 px-5 py-4 sm:px-6">
                                    <div className="flex min-w-0 items-center gap-3">
                                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white text-indigo-600 shadow-sm ring-1 ring-indigo-100">
                                            <MapPinIcon className="h-6 w-6" />
                                        </div>
                                        <div className="min-w-0">
                                            <Dialog.Title className="text-sm font-semibold text-indigo-800 uppercase">Assegnazione massiva aree</Dialog.Title>
                                            <p className="mt-0.5 text-xs text-gray-500">
                                                Assegna un'area di lavoro a più dipendenti per un periodo selezionato
                                            </p>
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        disabled={isSaving}
                                        className="rounded-md p-1.5 text-gray-400 transition hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        <XMarkIcon className="h-5 w-5" />
                                    </button>
                                </div>


                                {/* BODY */}
                                <div className="min-h-0 flex-1 space-y-5 overflow-y-auto bg-gray-50 px-5 py-5 sm:px-6">
                                    {error && (<Alert severity="error">{error}</Alert>)}
                                    {successMessage && (<Alert severity="success">{successMessage}</Alert>)}

                                    {/* STEP 1 - AREA */}
                                    <section className="overflow-visible rounded-lg border border-gray-200 bg-white shadow-md">
                                        <div className="border-b border-gray-100 px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-bold text-indigo-700">
                                                    1
                                                </div>
                                                <div>
                                                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                                                        Area di lavoro
                                                    </h3>
                                                    <p className="mt-0.5 text-xs text-gray-500">
                                                        Seleziona l'area da assegnare
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-4">
                                            {isLoadingAreas ? (
                                                <div className="flex h-10 items-center gap-2 text-xs text-gray-500">
                                                    <CircularProgress size={17} />
                                                    Caricamento aree...
                                                </div>
                                            ) : (
                                                <div className="rounded-md border border-gray-200">
                                                    <ComboboxAutocomplete
                                                        options={workingAreas}
                                                        value={selectedWorkingArea}
                                                        onChange={(value) => setSelectedWorkingArea(value ?? null)}
                                                        displayValue={(area) => area?.name || ""}
                                                        placeholder="Seleziona area di lavoro..."
                                                        bgActiveColor="bg-indigo-100"
                                                        textActiveColor="text-gray-900"
                                                        positionOptions="absolute"
                                                        zIndex="z-[1500]"
                                                        renderOption={(area) => (
                                                            <div className="flex min-w-0 items-center justify-between gap-3">
                                                                <span className="truncate text-sm text-gray-700">
                                                                    {area.name}
                                                                </span>
                                                                {area.justification_code && (
                                                                    <span className="shrink-0 rounded bg-indigo-50 px-2 py-0.5 text-[9px] font-semibold text-indigo-700">
                                                                        {area.justification_code}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        )}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </section>

                                    {/* STEP 2 - PERIODO */}
                                    <section className="rounded-lg border border-gray-200 bg-white shadow-md">
                                        <div className="border-b border-gray-100 px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-bold text-indigo-700">
                                                    2
                                                </div>
                                                <div>
                                                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                                                        Periodo
                                                    </h3>
                                                    <p className="mt-0.5 text-xs text-gray-500">
                                                        Puoi selezionare un intervallo massimo di 31 giorni
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="p-4">
                                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                                <div>
                                                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                        Dal
                                                    </label>
                                                    <div className="relative">
                                                        <CalendarDaysIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                                        <input
                                                            type="date"
                                                            value={startDate}
                                                            onChange={(event) => {
                                                                const value = event.target.value;
                                                                setStartDate(value);
                                                                if (endDate && moment(endDate).diff(moment(value), "days") > 30) {
                                                                    setEndDate(moment(value).add(30, "days").format("YYYY-MM-DD"));
                                                                }
                                                            }}
                                                            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-indigo-500 focus:ring-indigo-500"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                        Al
                                                    </label>
                                                    <div className="relative">
                                                        <CalendarDaysIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                                        <input
                                                            type="date"
                                                            value={endDate}
                                                            min={startDate || undefined}
                                                            max={maxEndDate}
                                                            onChange={(event) => setEndDate(event.target.value)}
                                                            className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm text-gray-700 focus:border-indigo-500 focus:ring-indigo-500"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-3">
                                                {dateRangeError ? (
                                                    <p className="text-xs font-medium text-red-600">
                                                        {dateRangeError}
                                                    </p>
                                                ) : (
                                                    <div className="inline-flex items-center gap-1.5 rounded-md bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700">
                                                        <CalendarDaysIcon className="h-3.5 w-3.5" />
                                                        {selectedDays}{" "}
                                                        {selectedDays === 1 ? "giorno selezionato" : "giorni selezionati"}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </section>

                                    {/* STEP 3 - DIPENDENTI */}
                                    <section className="overflow-visible rounded-lg border border-gray-200 bg-white shadow-md">
                                        <div className="border-b border-gray-100 px-4 py-3">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-2">
                                                    <div className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-100 text-[11px] font-bold text-indigo-700">
                                                        3
                                                    </div>

                                                    <div>
                                                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                                                            Dipendenti
                                                        </h3>

                                                        <p className="mt-0.5 text-xs text-gray-500">
                                                            Aggiungi rapidamente i dipendenti di un'unità organizzativa oppure selezionali singolarmente
                                                        </p>
                                                    </div>
                                                </div>

                                                {selectedEmployees.length > 0 && (
                                                    <div className="shrink-0 rounded-md bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-700">
                                                        {selectedEmployees.length} selezionat
                                                        {selectedEmployees.length === 1 ? "o" : "i"}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="space-y-5 p-4">

                                            {/* UNITÀ ORGANIZZATIVA */}
                                            <div>
                                                <div className="mb-1.5 flex items-center justify-between">
                                                    <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                        Aggiungi da unità organizzativa
                                                    </label>

                                                    {isLoadingUnitEmployees && (
                                                        <span className="flex items-center gap-1.5 text-[10px] text-indigo-600">
                                                            <CircularProgress size={11} />
                                                            Caricamento dipendenti...
                                                        </span>
                                                    )}
                                                </div>

                                                <div
                                                    className={`w-full rounded-lg border border-gray-200 shadow-sm ${
                                                        dateRangeError || isLoadingUnitEmployees
                                                            ? "pointer-events-none opacity-50"
                                                            : ""
                                                    }`}
                                                >
                                                    <ComboboxAutocomplete
                                                        options={units || []}
                                                        value={selectedUnit}
                                                        onChange={(next) => {
                                                            if (!next) return;

                                                            handleSelectUnit(next);
                                                        }}
                                                        placeholder="Seleziona unità organizzativa..."
                                                        bgActiveColor="bg-indigo-50"
                                                        textActiveColor="text-gray-700"
                                                        positionOptions="absolute"
                                                        zIndex="z-[1500]"
                                                        optionsClassName="border border-gray-300 max-h-36"
                                                        openDirection="bottom"
                                                        hideXMarkIcon
                                                    />
                                                </div>

                                                <p className="mt-1.5 text-[11px] leading-4 text-gray-500">
                                                    Seleziona un'unità organizzativa per aggiungere rapidamente i relativi dipendenti. Al termine la selezione dell'unità viene azzerata, mentre i dipendenti aggiunti restano disponibili nella selezione sottostante.
                                                </p>
                                            </div>

                                            {/* DIPENDENTI */}
                                            <div className="border-t border-gray-100 pt-4">
                                                <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                    Dipendenti selezionati
                                                </label>

                                                <div className={`rounded-lg border border-gray-200 ${dateRangeError ? "pointer-events-none opacity-50" : ""}`}>
                                                    <EmployeesMultiSelectSearchbar
                                                        token={token}
                                                        selectedEmployees={selectedEmployees}
                                                        onChange={setSelectedEmployees}
                                                        getEmployees={getEmployeesCb}
                                                        placeholder="Cerca o aggiungi un dipendente..."
                                                        employeeRolePairs={employeeRolePairs}
                                                        ou={null}
                                                        pageSize={10}
                                                        contractStartDate={startDate}
                                                        contractEndDate={endDate}
                                                        permission={permission}
                                                        openDirection="top"
                                                    />
                                                </div>

                                                {dateRangeError && (
                                                    <p className="mt-2 text-[11px] text-gray-400">
                                                        Imposta prima un periodo valido per selezionare i dipendenti.
                                                    </p>
                                                )}

                                                {selectedEmployees.length > 0 && (
                                                    <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3">
                                                        <span className="text-[11px] text-gray-500">
                                                            {selectedEmployees.length} dipendent
                                                            {selectedEmployees.length === 1 ? "e" : "i"} selezionat
                                                            {selectedEmployees.length === 1 ? "o" : "i"}
                                                        </span>

                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedEmployees([]);
                                                                setSelectedUnit(null);
                                                            }}
                                                            className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 hover:underline"
                                                        >
                                                            Deseleziona tutti
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </section>

                                    {/* COMPORTAMENTO ASSEGNAZIONI ESISTENTI */}
                                    <section className="rounded-lg border border-gray-200 bg-white p-4 shadow-md">
                                        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-700">
                                            Modalità assegnazione area
                                        </h3>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Scegli l'operazione da eseguire se sono già presenti delle aree assegnate
                                        </p>
                                        <div className="mt-3 space-y-2">
                                            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50">
                                                <input
                                                    type="radio"
                                                    name="assignmentMode"
                                                    value="empty_only"
                                                    checked={assignmentMode === "empty_only"}
                                                    onChange={() => setAssignmentMode("empty_only")}
                                                    className="mt-0.5 h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <div>
                                                    <p className="text-xs font-semibold uppercase text-gray-700">
                                                        Solo fasce senza area
                                                    </p>
                                                    <p className="mt-0.5 text-xs text-gray-500">
                                                        Le assegnazioni già presenti vengono mantenute
                                                    </p>
                                                </div>
                                            </label>

                                            <label className="flex cursor-pointer items-start gap-3 rounded-md border border-gray-200 p-3 hover:bg-gray-50">
                                                <input
                                                    type="radio"
                                                    name="assignmentMode"
                                                    value="overwrite"
                                                    checked={assignmentMode === "overwrite"}
                                                    onChange={() => setAssignmentMode("overwrite")}
                                                    className="mt-0.5 h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                                <div>
                                                    <p className="text-xs font-semibold uppercase text-gray-700">
                                                        Sostituisci aree esistenti
                                                    </p>
                                                    <p className="mt-0.5 text-xs text-gray-500">
                                                        L'area selezionata sostituirà quella eventualmente già assegnata alle fasce interessate
                                                    </p>
                                                </div>
                                            </label>
                                        </div>
                                    </section>

                                    {/* RIEPILOGO */}
                                    <section className={`rounded-lg border p-4 shadow-md ${canSubmit ? "border-indigo-200 bg-indigo-50/50" : "border-gray-200 bg-gray-100/60"}`}>
                                        <div className="flex items-start gap-3">
                                            <CheckCircleIcon className={`mt-0.5 h-5 w-5 shrink-0 ${canSubmit ? "text-indigo-600" : "text-gray-400"}`} />
                                            <div className="min-w-0 flex-1">
                                                <h3 className={`text-xs font-semibold uppercase tracking-wide ${canSubmit ? "text-indigo-800" : "text-gray-500"}`}>
                                                    Riepilogo assegnazione
                                                </h3>
                                                <div className="mt-2 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
                                                    <div>
                                                        <span className="block text-[10px] uppercase text-gray-400">
                                                            Area
                                                        </span>
                                                        <span className="font-semibold text-gray-700">
                                                            {selectedWorkingArea?.name || "Non selezionata"}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <span className="block text-[10px] uppercase text-gray-400">
                                                            Periodo
                                                        </span>
                                                        <span className="font-semibold text-gray-700">
                                                            {startDate && endDate
                                                                ? `${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`
                                                                : "Non selezionato"}
                                                        </span>
                                                        {!dateRangeError &&
                                                            selectedDays > 0 && (
                                                                <span className="ml-1 font-normal text-gray-500">
                                                                    · {selectedDays} gg
                                                                </span>
                                                            )}
                                                    </div>
                                                    <div>
                                                        <span className="block text-[10px] uppercase text-gray-400">
                                                            Dipendenti
                                                        </span>
                                                        <span className="font-semibold text-gray-700">
                                                            {selectedEmployees.length}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </section>
                                </div>


                                {/* FOOTER */}
                                <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-200 bg-gray-50/70 px-5 py-4 sm:flex-row sm:items-center sm:justify-end sm:px-6">
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        disabled={isSaving}
                                        className="rounded-md border border-gray-300 bg-white px-5 py-2 text-xs font-semibold uppercase text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Annulla
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={!canSubmit}
                                        className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-5 py-2 text-xs font-semibold uppercase text-white shadow-sm transition-colors hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-40"
                                    >
                                        {isSaving && (
                                            <CircularProgress size={15} color="inherit" />
                                        )}
                                        {isSaving ? "Assegnazione..." : "Applica area"}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

export default WorkingAreaBulkAssignmentModal;